import { ComponentProps, FC } from "react";
import { Col, Row, Tooltip, Typography, Space } from "antd";

import Icon, {
  MinusOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  QuestionOutlined,
  CloseOutlined
} from "@ant-design/icons";

import { classname } from "@ctra/utils";

import { UnchangedBars, IncreasedBars, DecreasedBars, UnchangedBarsInsufficient } from "../../..";
import styles from "./QnAProgress.module.less";

const { Text } = Typography;

export interface QnAProgressProps extends ComponentProps<typeof Row> {
  /**
   * title
   */
  title: string;
  /**
   * significant change
   */
  significant?: boolean;
  /**
   * significant change
   */
  insufficient?: boolean;
  /**
   * ratio change
   */
  ratioChange: number;
  /**
   * has increased
   */
  hasIncreased?: boolean;
  /**
   * tooltip data
   */
  tooltip?: {
    /**
     * before value
     */
    before: number;
    /**
     * after value
     */
    after: number;
    /**
     * before label text
     */
    beforeText: string;
    /**
     * after label text
     */
    afterText: string;
  };
  /**
   * empty state with greyed out text
   */
  empty?: boolean;
}

/**
 * Ask Ida tracker value progress
 * @param {string} title
 * @param {boolean | undefined} significant
 * @param {boolean | undefined} insufficient
 * @param {number} ratioChange
 * @param {boolean | undefined} hasIncreased
 * @param {{before: number, after: number, beforeText: string, afterText: string} | undefined} tooltip
 * @param {boolean | undefined} empty
 * @param {Pick<QnAProgressProps & {children?: React.ReactNode | undefined}, "gutter" | "align" | "justify" | "prefixCls" | "wrap" | "defaultChecked" | "defaultValue" | "suppressContentEditableWarning" | "suppressHydrationWarning" | "accessKey" | "className" | "contentEditable" | "contextMenu" | "dir" | "draggable" | "hidden" | "id" | "lang" | "placeholder" | "slot" | "spellCheck" | "style" | "tabIndex" | "translate" | "radioGroup" | "role" | "about" | "datatype" | "inlist" | "prefix" | "property" | "resource" | "typeof" | "vocab" | "autoCapitalize" | "autoCorrect" | "autoSave" | "color" | "itemProp" | "itemScope" | "itemType" | "itemID" | "itemRef" | "results" | "security" | "unselectable" | "inputMode" | "is" | "aria-activedescendant" | "aria-atomic" | "aria-autocomplete" | "aria-busy" | "aria-checked" | "aria-colcount" | "aria-colindex" | "aria-colspan" | "aria-controls" | "aria-current" | "aria-describedby" | "aria-details" | "aria-disabled" | "aria-dropeffect" | "aria-errormessage" | "aria-expanded" | "aria-flowto" | "aria-grabbed" | "aria-haspopup" | "aria-hidden" | "aria-invalid" | "aria-keyshortcuts" | "aria-label" | "aria-labelledby" | "aria-level" | "aria-live" | "aria-modal" | "aria-multiline" | "aria-multiselectable" | "aria-orientation" | "aria-owns" | "aria-placeholder" | "aria-posinset" | "aria-pressed" | "aria-readonly" | "aria-relevant" | "aria-required" | "aria-roledescription" | "aria-rowcount" | "aria-rowindex" | "aria-rowspan" | "aria-selected" | "aria-setsize" | "aria-sort" | "aria-valuemax" | "aria-valuemin" | "aria-valuenow" | "aria-valuetext" | "children" | "dangerouslySetInnerHTML" | "onCopy" | "onCopyCapture" | "onCut" | "onCutCapture" | "onPaste" | "onPasteCapture" | "onCompositionEnd" | "onCompositionEndCapture" | "onCompositionStart" | "onCompositionStartCapture" | "onCompositionUpdate" | "onCompositionUpdateCapture" | "onFocus" | "onFocusCapture" | "onBlur" | "onBlurCapture" | "onChange" | "onChangeCapture" | "onBeforeInput" | "onBeforeInputCapture" | "onInput" | "onInputCapture" | "onReset" | "onResetCapture" | "onSubmit" | "onSubmitCapture" | "onInvalid" | "onInvalidCapture" | "onLoad" | "onLoadCapture" | "onError" | "onErrorCapture" | "onKeyDown" | "onKeyDownCapture" | "onKeyPress" | "onKeyPressCapture" | "onKeyUp" | "onKeyUpCapture" | "onAbort" | "onAbortCapture" | "onCanPlay" | "onCanPlayCapture" | "onCanPlayThrough" | "onCanPlayThroughCapture" | "onDurationChange" | "onDurationChangeCapture" | "onEmptied" | "onEmptiedCapture" | "onEncrypted" | "onEncryptedCapture" | "onEnded" | "onEndedCapture" | "onLoadedData" | "onLoadedDataCapture" | "onLoadedMetadata" | "onLoadedMetadataCapture" | "onLoadStart" | "onLoadStartCapture" | "onPause" | "onPauseCapture" | "onPlay" | "onPlayCapture" | "onPlaying" | "onPlayingCapture" | "onProgress" | "onProgressCapture" | "onRateChange" | "onRateChangeCapture" | "onSeeked" | "onSeekedCapture" | "onSeeking" | "onSeekingCapture" | "onStalled" | "onStalledCapture" | "onSuspend" | "onSuspendCapture" | "onTimeUpdate" | "onTimeUpdateCapture" | "onVolumeChange" | "onVolumeChangeCapture" | "onWaiting" | "onWaitingCapture" | "onAuxClick" | "onAuxClickCapture" | "onClick" | "onClickCapture" | "onContextMenu" | "onContextMenuCapture" | "onDoubleClick" | "onDoubleClickCapture" | "onDrag" | "onDragCapture" | "onDragEnd" | "onDragEndCapture" | "onDragEnter" | "onDragEnterCapture" | "onDragExit" | "onDragExitCapture" | "onDragLeave" | "onDragLeaveCapture" | "onDragOver" | "onDragOverCapture" | "onDragStart" | "onDragStartCapture" | "onDrop" | "onDropCapture" | "onMouseDown" | "onMouseDownCapture" | "onMouseEnter" | "onMouseLeave" | "onMouseMove" | "onMouseMoveCapture" | "onMouseOut" | "onMouseOutCapture" | "onMouseOver" | "onMouseOverCapture" | "onMouseUp" | "onMouseUpCapture" | "onSelect" | "onSelectCapture" | "onTouchCancel" | "onTouchCancelCapture" | "onTouchEnd" | "onTouchEndCapture" | "onTouchMove" | "onTouchMoveCapture" | "onTouchStart" | "onTouchStartCapture" | "onPointerDown" | "onPointerDownCapture" | "onPointerMove" | "onPointerMoveCapture" | "onPointerUp" | "onPointerUpCapture" | "onPointerCancel" | "onPointerCancelCapture" | "onPointerEnter" | "onPointerEnterCapture" | "onPointerLeave" | "onPointerLeaveCapture" | "onPointerOver" | "onPointerOverCapture" | "onPointerOut" | "onPointerOutCapture" | "onGotPointerCapture" | "onGotPointerCaptureCapture" | "onLostPointerCapture" | "onLostPointerCaptureCapture" | "onScroll" | "onScrollCapture" | "onWheel" | "onWheelCapture" | "onAnimationStart" | "onAnimationStartCapture" | "onAnimationEnd" | "onAnimationEndCapture" | "onAnimationIteration" | "onAnimationIterationCapture" | "onTransitionEnd" | "onTransitionEndCapture" | "ref" | "key">} rest
 * @return {JSX.Element}
 * @constructor
 */
const QnAProgress: FC<QnAProgressProps> = ({
  title,
  significant = true,
  insufficient = false,
  ratioChange,
  hasIncreased = true,
  tooltip,
  empty,
  ...rest
}) => {
  const tooltipContent = tooltip && (
    <>
      <Row wrap={false}>
        <Col span={8}>
          <Text>{tooltip.beforeText}</Text>
        </Col>
        <Col>
          <Text strong>{tooltip.before.toFixed(1)}</Text>
        </Col>
      </Row>
      <Row wrap={false}>
        <Col span={8}>
          <Text>{tooltip.afterText}</Text>
        </Col>
        <Col>
          <Row gutter={5}>
            <Col>
              <Text strong>{tooltip.after.toFixed(1)}</Text>
            </Col>
            <Col>
              <Text strong>({ratioChange.toFixed(1)}%)</Text>
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  );

  const increased = (
    <Space align="center" direction="vertical" className={styles.IconWrapper}>
      <ArrowUpOutlined className={styles.Arrow} />
      <Icon component={IncreasedBars} />
    </Space>
  );

  const decreased = (
    <Space align="center" direction="vertical" className={styles.IconWrapper}>
      <ArrowDownOutlined className={styles.Arrow} />
      <Icon component={DecreasedBars} />
    </Space>
  );

  const insignificant = (
    <Space
      align="center"
      direction="vertical"
      className={classname(styles.IconWrapper, styles.Insignificant)}
    >
      <MinusOutlined className={styles.Arrow} />
      <Icon component={UnchangedBars} />
    </Space>
  );

  const insufficientEffect = (
    <Space
      align="center"
      direction="vertical"
      className={classname(styles.IconWrapper, styles.Insignificant, empty ? styles.Hidden : null)}
    >
      {empty ? <CloseOutlined className={styles.Arrow} /> : <QuestionOutlined className={styles.Arrow} />}
      <Icon component={UnchangedBarsInsufficient} />
    </Space>
  );

  return (
    <Row
      gutter={[8, 8]}
      align="middle"
      justify="center"
      className={classname(styles.Wrapper, "ctra-askIda-QnAProgress")}
      {...rest}
    >
      <Col>
        <Text type="secondary" className={classname(empty ? styles.Empty : null)}>
          {title}
        </Text>
      </Col>
      {ratioChange ? (
        <Col>
          <Tooltip overlayClassName={styles.Tooltip} placement="bottom" title={tooltipContent}>
            {significant ? (hasIncreased ? increased : decreased) : insignificant}
          </Tooltip>
        </Col>
      ) : (
        <Col>{empty || insufficient ? insufficientEffect : <Text type="secondary">--</Text>}</Col>
      )}
    </Row>
  );
};

export default QnAProgress;
